import type { DataGridColumnProps } from '../../components/DataGrid/DataGrid';
import type { DataGridGetRowLinkOptionsFn } from '../../components/DataGrid/DroppableCell/DroppableCell';
import type { DataGridHeaderFilter } from '../../components/DataGrid/HeaderFilter/HeaderFilter';
import type { DataGridColumnOptionsProps } from '../../components/DataGridControl/DataGridControl';
import type { SortContentObjectsLocalStorage } from '../../types/localStorage';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { exportSpacesCsv } from '../../lib/flotiq-client/api-helpers';
import {
  getStripeURL,
  getTestProps,
  prepareColumns,
  setInitialGridOptions,
} from '../../lib/helpers';

import CustomizableDataGrid from '../../components/CustomizableDataGrid/CustomizableDataGrid';
import { useGridFilters } from '../../components/DataGrid/useGridFilters';
import DataGridControl from '../../components/DataGridControl/DataGridControl';
import Heading from '../../components/Heading/Heading';
import Loader from '../../components/Loader/Loader';
import StatusBar from '../../components/StatusBar/StatusBar';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionButton from '../../components/Topbar/buttons/base/TopbarActionButton';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';

import { useAllSpaces } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';

import UserContext from '../../contexts/UserContext';

import { WarningIcon } from '../../images/shapes';

import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import { getDefinedColumns } from './definedColumns';

const getApiFilters = (filters: DataGridHeaderFilter) => {
  if (!filters) return {};

  const newFilters: Record<string, string | string[]> = {};

  Object.keys(filters).forEach((key) => {
    if (key === 'organization.name') {
      newFilters.organizationName = filters['organization.name'].filter;
    } else if (key === 'organization.id') {
      newFilters.organizationId = filters['organization.id'].filter;
    } else if (key === 'organization.paymentDueDate') {
      newFilters.organizationPaymentDueDate =
        filters['organization.paymentDueDate'].filter;
    } else {
      newFilters[key] = filters[key].filter;
    }
  });

  return newFilters;
};

const testId = 'all-spaces';

const Spaces = () => {
  const SORT_KEY = 'cms.all-spaces-sort';
  const OPTIONS_KEY = 'cms.all-spaces-grid-state';

  const spacesGridContainer = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { isRoleAdmin } = use(UserContext);
  const [searchParams] = useSearchParams();

  const [firstLoading, setFirstLoading] = useState(true);
  const [initDataHasContent, setInitDataHasContent] = useState(false);
  const [editGrid, setEditGrid] = useState(false);
  const [filterGrid, setFilterGrid] = useState(!!searchParams.get('filters'));
  const [columns, setColumns] = useState<DataGridColumnProps[]>([]);

  const [sort, setSort, { removeItem: removeSort }] =
    useLocalStorageState<SortContentObjectsLocalStorage>(SORT_KEY);
  const [gridOptions, setGridOptions, { removeItem: removeGridOptions }] =
    useLocalStorageState<DataGridColumnOptionsProps[]>(OPTIONS_KEY);

  const {
    filters,
    filtersApplied,
    handleFiltersChange,
    page,
    handlePageChange,
    limit,
    handleLimitChange,
    removeAllFilters,
  } = useGridFilters('spaces');

  const spacesParams = useMemo(
    () => ({
      page: page,
      limit: limit,
      ...getApiFilters(filters),
      ...(sort?.sortOrder ? { order_direction: sort?.sortOrder } : {}),
      ...(sort?.sortField ? { order_by: sort?.sortField } : {}),
    }),
    [page, limit, filters, sort?.sortOrder, sort?.sortField],
  );

  const allSpacesOptions = useMemo(
    () => ({
      pause: !isRoleAdmin,
    }),
    [isRoleAdmin],
  );

  const {
    data: allSpaces,
    errors: allSpacesError,
    status: allSpacesStatus,
    isLoading: allSpacesAreLoading,
    pagination,
    reload: allSpacesReload,
  } = useAllSpaces(spacesParams, allSpacesOptions);

  useApiErrorsToast(allSpacesError);

  useEffect(() => {
    if (pagination?.total_pages < page)
      handlePageChange(pagination.total_pages);
  }, [handlePageChange, page, pagination?.total_pages]);

  useEffect(() => {
    if (firstLoading) {
      setInitDataHasContent(false);
    }
    if (!allSpacesAreLoading) setFirstLoading(false);
  }, [allSpacesAreLoading, firstLoading]);

  useEffect(() => {
    if (!initDataHasContent && allSpaces.length) {
      setInitDataHasContent(true);
    }
  }, [initDataHasContent, allSpaces.length]);

  const handleResetGrid = useCallback(() => {
    setSort(undefined);
    removeAllFilters();
    removeSort();
    removeGridOptions();
    setGridOptions(undefined);
  }, [
    removeAllFilters,
    removeGridOptions,
    removeSort,
    setGridOptions,
    setSort,
  ]);

  const showGrid = useMemo(() => {
    return (
      isRoleAdmin &&
      (allSpaces.length > 0 ||
        (allSpacesAreLoading && !firstLoading) ||
        filtersApplied ||
        initDataHasContent)
    );
  }, [
    firstLoading,
    filtersApplied,
    initDataHasContent,
    isRoleAdmin,
    allSpaces.length,
    allSpacesAreLoading,
  ]);

  const emptyData = useMemo(() => {
    if (showGrid) return null;
    if (firstLoading && isRoleAdmin) {
      return (
        <Loader
          size="big"
          type="spinner-grid"
          {...getTestProps(testId, 'loading', 'testId')}
        />
      );
    }
    return (
      <Heading
        level={2}
        additionalClasses="text-3xl md:text-4xl leading-8 dark:text-white"
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon className="text-red w-14 md:w-20 mb-3" />
          {allSpacesStatus === 403 || !isRoleAdmin
            ? t('Global.NoAccess')
            : t('Media.OnErrorMessage')}
        </div>
      </Heading>
    );
  }, [showGrid, firstLoading, isRoleAdmin, allSpacesStatus, t]);

  const handleInitialGridOptions = useCallback(
    (cols: DataGridColumnProps[], minWidth: number) => {
      setInitialGridOptions(
        cols,
        minWidth,
        spacesGridContainer.current?.offsetWidth || 0,
        setGridOptions,
        setColumns,
      );
    },
    [setGridOptions],
  );

  useEffect(() => {
    prepareColumns(
      gridOptions,
      editGrid,
      t,
      testId,
      setGridOptions,
      handleInitialGridOptions,
      setColumns,
      getDefinedColumns,
    );
  }, [t, handleInitialGridOptions, gridOptions, editGrid, setGridOptions]);

  const getRowLinkOptions = useCallback<DataGridGetRowLinkOptionsFn>(
    (accessor, rowData) =>
      accessor === 'subscriptionId'
        ? {
            to: getStripeURL(`/subscriptions/${rowData.subscriptionId}`),
            target: '_blank',
            rel: 'noreferrer',
          }
        : { to: `/spaces/edit/${rowData.id}` },
    [],
  );

  const [isExporting, setIsExporting] = useState('');

  const onExportClick = async (key: string, filters?: Record<string, any>) => {
    setIsExporting(key);
    await exportSpacesCsv(filters, key);
    setIsExporting('');
  };

  return (
    <PageLayout
      page="admin/all-spaces"
      menuItemOpen="admin"
      title={t('Global.AllSpaces')}
      breadcrumbs={<TopbarBreadcrumbs />}
      buttons={
        <>
          <TopbarButton
            label={t('Users.Export')}
            onClick={() => onExportClick('spaces')}
            disabled={isExporting === 'spaces'}
          />
          <TopbarActionMenu>
            <TopbarActionButton
              label={t('Users.ExportCancelled')}
              onClick={() =>
                onExportClick('canceled-spaces', {
                  subscriptionStatus: 'canceled',
                })
              }
              disabled={isExporting === 'canceled-spaces'}
            />
          </TopbarActionMenu>
        </>
      }
      secondaryTopbar={
        <DataGridControl
          displayGridCheckboxes={showGrid && columns.length > 0}
          displayGridControl={showGrid}
          editGrid={editGrid}
          filterGrid={filterGrid}
          setEditGrid={setEditGrid}
          setFilterGrid={setFilterGrid}
          handleResetGrid={handleResetGrid}
          limit={limit}
          handleLimitChange={handleLimitChange}
          columns={columns}
          gridOptions={gridOptions}
          optionsLocalStorageKey={OPTIONS_KEY}
          setGridOptions={setGridOptions}
          columnsVisibilityAdditionalClasses={twMerge('-top-2 xs:top-[0.2rem]')}
          testId={testId}
        />
      }
      testId={testId}
      noPaddings
    >
      <div className={predefinedLayoutClasses.withDataGrid}>
        <div
          className={predefinedLayoutClasses.dataGridContainer}
          ref={spacesGridContainer}
        >
          {showGrid ? (
            <CustomizableDataGrid
              columns={columns}
              setColumns={setColumns}
              data={allSpaces}
              isLoading={allSpacesAreLoading}
              sort={sort?.sortField}
              sortOrder={sort?.sortOrder}
              sortingLocalStorageKey={SORT_KEY}
              optionsLocalStorageKey={OPTIONS_KEY}
              setCurrentPage={handlePageChange}
              editGrid={editGrid}
              noDataInfoText={t('Spaces.FiltersEmptyResult')}
              hasFilters={filterGrid}
              filters={filters}
              disableFilters={editGrid}
              onFilter={handleFiltersChange}
              statusBar={
                <StatusBar
                  rows={pagination.count}
                  currentPage={page}
                  pagesCount={pagination.total_pages}
                  handlePageChange={handlePageChange}
                  handleDataUpdate={allSpacesReload}
                  resultsFrom={(pagination.current_page - 1) * limit + 1}
                  resultsTo={
                    (pagination.current_page - 1) * limit + pagination.count
                  }
                  resultsTotalCount={pagination.total_count}
                  {...getTestProps(testId, 'statusbar', 'testId')}
                />
              }
              getRowLinkOptions={getRowLinkOptions}
              additionalClasses="bg-white dark:bg-slate-900"
              {...getTestProps(testId, 'grid', 'testId')}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              {emptyData}
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default Spaces;
